import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  StarIcon as StarSolidIcon, 
  HeartIcon, 
  ArrowDownTrayIcon,
  LinkIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  ClockIcon,
  TagIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { usePubPackages } from '../../contexts/PubPackageContext';
import { PubPackage } from '../../firebase/pubPackageService';
import { Link } from 'react-router-dom';

const PubPackagesPage: React.FC = () => {
  const { pubPackages, featuredPubPackages, loading, error } = usePubPackages();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'downloads' | 'likes' | 'pubPoints' | 'date'>('downloads');
  const [selectedPackage, setSelectedPackage] = useState<PubPackage | null>(null);

  const platformOptions = [
    { value: 'all', label: 'All Platforms', icon: GlobeAltIcon },
    { value: 'web', label: 'Web', icon: GlobeAltIcon },
    { value: 'android', label: 'Android', icon: DevicePhoneMobileIcon },
    { value: 'ios', label: 'iOS', icon: DevicePhoneMobileIcon },
    { value: 'windows', label: 'Windows', icon: ComputerDesktopIcon },
    { value: 'macos', label: 'macOS', icon: ComputerDesktopIcon },
    { value: 'linux', label: 'Linux', icon: ComputerDesktopIcon },
  ];

  const filteredPackages = pubPackages
    .filter(pkg => {
      const searchMatch = searchTerm === '' || 
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const platformMatch = selectedPlatform === 'all' || 
        pkg.platforms.includes(selectedPlatform);
      
      return searchMatch && platformMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'downloads':
          return (b.downloads || 0) - (a.downloads || 0);
        case 'likes':
          return (b.likes || 0) - (a.likes || 0);
        case 'pubPoints':
          return (b.pubPoints || 0) - (a.pubPoints || 0);
        case 'date':
          return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
        default:
          return 0;
      }
    });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'web':
        return <GlobeAltIcon className="h-4 w-4" />;
      case 'android':
      case 'ios':
        return <DevicePhoneMobileIcon className="h-4 w-4" />;
      case 'windows':
      case 'macos':
      case 'linux':
        return <ComputerDesktopIcon className="h-4 w-4" />;
      default:
        return <GlobeAltIcon className="h-4 w-4" />;
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getPopularityBadge = (popularity: number) => {
    if (popularity >= 90) return { label: 'Very Popular', color: 'bg-green-100 text-green-800' };
    if (popularity >= 70) return { label: 'Popular', color: 'bg-blue-100 text-blue-800' };
    if (popularity >= 50) return { label: 'Growing', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'New', color: 'bg-gray-100 text-gray-800' };
  };

  const getPubPointsBadge = (points: number) => {
    if (points >= 130) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (points >= 100) return { label: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (points >= 70) return { label: 'Fair', color: 'bg-yellow-100 text-yellow-800' };
    if (points >= 40) return { label: 'Needs Work', color: 'bg-orange-100 text-orange-800' };
    return { label: 'Poor', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || pubPackages.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="mx-auto h-32 w-32 text-blue-400 mb-8">
              <CodeBracketIcon className="h-full w-full" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Flutter Packages Coming Soon!
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              I'm working on publishing amazing Flutter packages to help developers build better apps. 
              Check back soon for exciting new packages!
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-2">
                🚀 What's Coming
              </h3>
              <ul className="text-left text-blue-800 dark:text-blue-400 space-y-2">
                <li>• State management utilities</li>
                <li>• Custom UI components</li>
                <li>• Development tools and helpers</li>
                <li>• Performance optimization packages</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/taruncse"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <CodeBracketIcon className="h-5 w-5 mr-2" />
                Follow on GitHub
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Get Notified
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Flutter
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                {" "}Pub Packages
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Discover my collection of open-source Flutter packages that help developers build amazing apps faster
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <CodeBracketIcon className="h-6 w-6" />
                <span className="text-lg font-medium">{pubPackages.length} Packages</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowDownTrayIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {formatNumber(pubPackages.reduce((total, pkg) => total + (pkg.downloads || 0), 0))} Downloads
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <HeartIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {pubPackages.reduce((total, pkg) => total + (pkg.likes || 0), 0)} Likes
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <StarSolidIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {Math.round(pubPackages.reduce((total, pkg) => total + (pkg.pubPoints || 0), 0) / pubPackages.length) || 0} Avg Points
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured Packages */}
      {featuredPubPackages.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                ⭐ Featured Packages
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                My most popular and useful Flutter packages
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {featuredPubPackages.slice(0, 6).map((pkg) => (
                <motion.div
                  key={pkg.id}
                  variants={itemVariants}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer"
                  onClick={() => {
                    console.log('Selected featured package:', pkg);
                    console.log('Featured package screenshots:', pkg.screenshots);
                    setSelectedPackage(pkg);
                  }}
                >
                  {/* Package Header */}
                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      {pkg.icon ? (
                        <img
                          src={pkg.icon}
                          alt={pkg.name}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <CodeBracketIcon className="h-8 w-8 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {pkg.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          v{pkg.version}
                        </p>
                      </div>
                      <StarSolidIcon className="h-6 w-6 text-yellow-400" />
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                      {pkg.description}
                    </p>

                    {/* Platform Support */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {pkg.platforms.slice(0, 4).map((platform) => (
                        <span
                          key={platform}
                          className="inline-flex items-center space-x-1 px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium"
                        >
                          {getPlatformIcon(platform)}
                          <span className="capitalize">{platform}</span>
                        </span>
                      ))}
                      {pkg.platforms.length > 4 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
                          +{pkg.platforms.length - 4} more
                        </span>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          <span>{formatNumber(pkg.downloads || 0)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="h-4 w-4" />
                          <span>{pkg.likes || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                          <span>{pkg.pubPoints || 0}</span>
                        </div>
                      </div>
                      {pkg.popularity && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPopularityBadge(pkg.popularity).color}`}>
                          {getPopularityBadge(pkg.popularity).label}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* All Packages Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              All Packages
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Explore my complete collection of Flutter packages
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Packages
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Search by name, description, or tags..."
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Platform Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Platform
                </label>
                <div className="relative">
                  <select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white appearance-none"
                  >
                    {platformOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FunnelIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="downloads">Most Downloaded</option>
                  <option value="likes">Most Liked</option>
                  <option value="pubPoints">Highest Pub Points</option>
                  <option value="date">Latest</option>
                  <option value="name">Name A-Z</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Packages Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredPackages.map((pkg) => (
              <motion.div
                key={pkg.id}
                variants={itemVariants}
                                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer"
                  onClick={() => {
                    console.log('Selected package:', pkg);
                    console.log('Package screenshots:', pkg.screenshots);
                    setSelectedPackage(pkg);
                  }}
              >
                <div className="p-6">
                  {/* Package Header */}
                  <div className="flex items-center space-x-4 mb-4">
                    {pkg.icon ? (
                      <img
                        src={pkg.icon}
                        alt={pkg.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <CodeBracketIcon className="h-6 w-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {pkg.name}
                        </h3>
                        {pkg.featured && <StarSolidIcon className="h-5 w-5 text-yellow-400" />}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        v{pkg.version}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {pkg.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {pkg.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                    {pkg.tags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                        +{pkg.tags.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Platform Support */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {pkg.platforms.slice(0, 6).map((platform) => (
                      <span
                        key={platform}
                        className="inline-flex items-center space-x-1 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs"
                      >
                        {getPlatformIcon(platform)}
                        <span className="capitalize">{platform}</span>
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>{formatNumber(pkg.downloads || 0)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <HeartIcon className="h-4 w-4" />
                        <span>{pkg.likes || 0}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                        <span>{pkg.pubPoints || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {pkg.githubUrl && (
                        <a
                          href={pkg.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          <CodeBracketIcon className="h-5 w-5" />
                        </a>
                      )}
                      <a
                        href={pkg.pubDevUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredPackages.length === 0 && (
            <div className="text-center py-12">
              <CodeBracketIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No packages found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Package Detail Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedPackage(null)}
            />

            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setSelectedPackage(null)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    {/* Package Header */}
                    <div className="flex items-center space-x-6 mb-6">
                      {selectedPackage.icon ? (
                        <img
                          src={selectedPackage.icon}
                          alt={selectedPackage.name}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <CodeBracketIcon className="h-10 w-10 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                            {selectedPackage.name}
                          </h3>
                          {selectedPackage.featured && <StarSolidIcon className="h-8 w-8 text-yellow-400" />}
                        </div>
                        <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                          v{selectedPackage.version} • by {selectedPackage.author}
                        </p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            <span>{formatNumber(selectedPackage.downloads || 0)} downloads</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <HeartIcon className="h-5 w-5" />
                            <span>{selectedPackage.likes || 0} likes</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <StarSolidIcon className="h-5 w-5 text-yellow-500" />
                            <span>{selectedPackage.pubPoints || 0} pub points</span>
                            {selectedPackage.pubPoints && selectedPackage.pubPoints > 0 && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPubPointsBadge(selectedPackage.pubPoints).color}`}>
                                {getPubPointsBadge(selectedPackage.pubPoints).label}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="h-5 w-5" />
                            <span>Published {new Date(selectedPackage.publishedDate).getFullYear()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex space-x-4 mb-6">
                      <a
                        href={selectedPackage.pubDevUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                        View on pub.dev
                      </a>
                      {selectedPackage.githubUrl && (
                        <a
                          href={selectedPackage.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          <CodeBracketIcon className="h-5 w-5 mr-2" />
                          Source Code
                        </a>
                      )}
                      {selectedPackage.documentationUrl && (
                        <a
                          href={selectedPackage.documentationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          <DocumentTextIcon className="h-5 w-5 mr-2" />
                          Documentation
                        </a>
                      )}
                    </div>

                    {/* Description */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Description
                      </h4>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {selectedPackage.longDescription}
                      </p>
                    </div>

                    {/* Grid Layout for Features, Platforms, etc. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Features */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          ✨ Key Features
                        </h4>
                        <ul className="space-y-2">
                          {selectedPackage.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <CheckBadgeIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Supported Platforms */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          🚀 Supported Platforms
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedPackage.platforms.map((platform) => (
                            <div
                              key={platform}
                              className="flex items-center space-x-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            >
                              {getPlatformIcon(platform)}
                              <span className="capitalize font-medium">{platform}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        🏷️ Tags
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedPackage.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Screenshots */}
                    {selectedPackage.screenshots && selectedPackage.screenshots.length > 0 ? (
                      <div className="mt-8">
                        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                          📱 App Screenshots ({selectedPackage.screenshots.length})
                        </h4>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {selectedPackage.screenshots.map((screenshot, index) => (
                              <div key={index} className="relative group">
                                {/* Mobile Frame */}
                                <div className="relative mx-auto w-64 h-auto bg-gray-900 dark:bg-gray-100 rounded-3xl p-3 shadow-2xl">
                                  {/* Phone notch/speaker */}
                                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-700 dark:bg-gray-300 rounded-full"></div>
                                  
                                  {/* Screen */}
                                  <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16' }}>
                                    <img
                                      src={screenshot}
                                      alt={`Screenshot ${index + 1}`}
                                      className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                                      onError={(e) => {
                                        console.error(`Failed to load screenshot ${index + 1}:`, screenshot);
                                        e.currentTarget.style.display = 'none';
                                      }}
                                      onLoad={() => {
                                        console.log(`Successfully loaded screenshot ${index + 1}:`, screenshot);
                                      }}
                                      onClick={() => {
                                        // Open screenshot in larger view
                                        window.open(screenshot, '_blank');
                                      }}
                                    />
                                    
                                    {/* Overlay on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center">
                                        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">
                                          <p className="text-sm font-medium">Click to view full size</p>
                                          <p className="text-xs opacity-80">Screenshot {index + 1}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Home indicator */}
                                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                                </div>
                                
                                {/* Screenshot title/number */}
                                <div className="text-center mt-3">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                    Screenshot {index + 1}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* View all screenshots button if many */}
                          {selectedPackage.screenshots.length > 6 && (
                            <div className="text-center mt-6">
                              <button
                                onClick={() => {
                                  // Could implement a lightbox gallery here
                                  selectedPackage.screenshots?.forEach((screenshot, index) => {
                                    setTimeout(() => window.open(screenshot, '_blank'), index * 200);
                                  });
                                }}
                                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                              >
                                📱 View All Screenshots
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          📱 Screenshots
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No screenshots available for this package.
                        </p>
                        {/* Debug info */}
                        <details className="mt-2">
                          <summary className="text-xs text-gray-400 cursor-pointer">Debug Info</summary>
                          <pre className="text-xs text-gray-400 mt-1">
                            {JSON.stringify({
                              hasScreenshots: !!selectedPackage.screenshots,
                              screenshotsLength: selectedPackage.screenshots?.length || 0,
                              screenshotsArray: selectedPackage.screenshots
                            }, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}

                    {/* Technical Details */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          🔧 Technical Details
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">License:</span>
                            <span className="text-gray-900 dark:text-white">{selectedPackage.license}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Dart SDK:</span>
                            <span className="text-gray-900 dark:text-white">{selectedPackage.dartSdkConstraint}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Flutter SDK:</span>
                            <span className="text-gray-900 dark:text-white">{selectedPackage.flutterSdkConstraint}</span>
                          </div>
                        </div>
                      </div>

                      {selectedPackage.dependencies && selectedPackage.dependencies.length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                            📦 Dependencies
                          </h4>
                          <ul className="space-y-1">
                            {selectedPackage.dependencies.slice(0, 5).map((dep, index) => (
                              <li key={index} className="text-gray-600 dark:text-gray-300">
                                • {dep}
                              </li>
                            ))}
                            {selectedPackage.dependencies.length > 5 && (
                              <li className="text-gray-500 dark:text-gray-400">
                                +{selectedPackage.dependencies.length - 5} more...
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PubPackagesPage; 